import { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import surePassIcon from '../../../../assets/surePassIcon.svg';
import DialogConfetti from '../../../../components/confirmation/DialogConfetti';
import PrimaryButton from '../../../../components/buttons/PrimaryButton';
import { OfficialData, sharePin } from '../../../../networkRequest/officialsService';
import { useUser } from '../../../../hooks/useUser';
import { Toast } from '@components';
import Utils from '../../../../utils/utils';

interface AddAgencyOfficialDialogCongratulationsProps {
  onClose: () => void;
  newOfficial: OfficialData;
}

const AddAgencyOfficialDialogCongratulations = ({
  onClose,
  newOfficial
}: AddAgencyOfficialDialogCongratulationsProps) => {
  const [emails, setEmails] = useState(newOfficial.email ?? "");
  const theme = useTheme();
  const padding = theme.spacing(3);
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const { user } = useUser();

  const shareOfficialPin = async () => {
    const response = await sharePin(user!.accessToken, newOfficial.id, Utils.parseCommaSeparatedList(emails))
    if (response.error) {
      setErrorMessage(response.error)
    } else {
      setEmails(newOfficial.email ?? "")
      setSuccessMessage("Verifier PIN has been shared");
    }
  }

  const height = newOfficial.pin ? undefined : '60vh'
  const paddingTop = newOfficial.pin ? '120px' : undefined

  return (
    <DialogContent sx={{ padding }}>
      {errorMessage && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      <Grid
        container
        spacing={1}
        sx={{
          height: height,
          backgroundColor: theme.palette.grey[50],
          borderRadius: theme.shape.borderRadius,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          padding: '24px',
          paddingTop: paddingTop
        }}
      >
        <Box component="img" src={surePassIcon} alt="surepass logo" sx={{ width: '8%' }} />
        {/* variant h4 is too small, variant h3 is too big */}
        <Typography sx={{ fontSize: '2.5rem' }}>Congratulations!</Typography>
        <Grid size={4}>
          <Typography sx={{ textAlign: 'center' }}>
            {`${newOfficial?.first_name} ${newOfficial?.last_name}`} has been added as {Utils.withArticle(newOfficial.role.name)}
          </Typography>
        </Grid>
        <DialogConfetti pieces={50} />

        {newOfficial.pin &&
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderWidth: 1,
              borderColor: 'black',
              borderStyle: 'solid',
              borderRadius: '10px',
              padding: '12px',
              marginTop: '48px'
            }}
          >
            <Typography
              variant="h6"
            >
              Verifier PIN
            </Typography>

            <Typography
              variant="h4"
            >
              {newOfficial.pin}
            </Typography>

            <Typography
              variant="h6"
              style={{
                fontSize: '12px'
              }}
            >
              The agency is responsible for securely sharing and protecting the PIN.
            </Typography>
          </Box>
        }
      </Grid>

      {newOfficial.pin ?
        <Box>
          <Divider sx={{ marginTop: '32px' }} />

          <Typography
            variant="h6"
            sx={{
              marginTop: '16px',
              marginBottom: '16px'
            }}
          >
            Share Verifier PIN
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            sx={{ width: "100%" }}
          >
            <TextField
              label="Email"
              placeholder="Emails, comma separated"
              defaultValue={emails || undefined}
              onChange={(e) => setEmails(e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px"
                }
              }}
            />

            <Button
              variant="outlined"
              onClick={shareOfficialPin}
              disabled={!user}
              sx={{
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.primary,
                borderRadius: "10px",
                whiteSpace: "nowrap",
                fontSize: "14px",
                fontWeight: 600,
                paddingLeft: "24px",
                paddingRight: "24px"
              }}
            >
              SHARE PIN
            </Button>
          </Stack>
        </Box>
        :
        <DialogActions sx={{ paddingTop: padding, paddingX: padding }}>
          <PrimaryButton action={onClose} disabled={false} loading={false}>
            Close
          </PrimaryButton>
        </DialogActions>
      }



    </DialogContent>
  );
};

export default AddAgencyOfficialDialogCongratulations;
